import { useQuery } from "@tanstack/react-query"
import {
  getYourProfileQueryKey,
  useGetYourProfile,
} from "#/lib/api/react-query/useGetYourProfile"
import type { ProtectedProfile } from "#/lib/api/types/ProtectedProfile"
import { decryptWithSymmetricKey } from "#/lib/encryption/crypto"
import { useCiphers } from "./use-ciphers"
import { useSession } from "#/lib/auth/session-context"

// Decrypted Profile
export type Profile = {
  bio: string
}

/** useYourProfile returns the decrypted profile data */
export function useYourProfile() {
  const { session } = useSession()
  const { data: ciphers } = useCiphers()
  const { data: encryptedProfile } = useGetYourProfile({
    query: {
      enabled: !!session && !!ciphers,
    },
  })

  return useQuery({
    queryKey: [
      getYourProfileQueryKey()[0],
      "decrypted",
      encryptedProfile,
      ciphers?.profileSymmetricKey,
    ],
    queryFn: () =>
      decryptProfile(encryptedProfile, ciphers?.profileSymmetricKey),
    enabled: !!ciphers && typeof encryptedProfile !== "undefined",
  })
}

async function decryptProfile(
  encryptedProfile: ProtectedProfile | undefined,
  profileSymmetricKey: CryptoKey | undefined,
): Promise<Profile> {
  if (typeof encryptedProfile === "undefined" || !profileSymmetricKey) {
    // Won't happen due to the enabled check
    throw new Error("impossible state")
  }
  if (encryptedProfile.bio === "") {
    return { bio: "" }
  }
  let bio = ""
  if (encryptedProfile.bio) {
    const decrypted = await decryptWithSymmetricKey(
      encryptedProfile.bio,
      profileSymmetricKey,
    )
    bio = new TextDecoder().decode(decrypted)
  }
  const profile = { bio }
  return profile
}
